import { useMemo } from 'react'

import {
  matchFilterKey, matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { IntegrationModel, IntegrationRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['integrations']

type IntegrationFilters = {
  integrable_type?: string,
  integrable_id?: number,
  service_provider?: string,
}

type UseIntegrationsOptions = {
  filters?: IntegrationFilters,
  performHttpRequests?: boolean,
  requestOptions?: IntegrationRequestOptions,
}

function useIntegrations(options: UseIntegrationsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    integrable_type: filterIntegrableType,
    integrable_id: filterIntegrableId,
    service_provider: filterServiceProvider,
  } = filters

  const {
    updatedEntities: { integrations: integrationsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { integrations } = useSelector(reduxState => reduxState.entities)

  const filteredIntegrations = useMemo(() => {
    const filtered = Object.values(integrations).filter((integration: IntegrationModel) => {
      const { integrable_id, integrable_type, service_provider } = integration

      const matchServiceProvider = matchFilterString(service_provider, filterServiceProvider)

      const matchIntegrableId = matchFilterNumber(integrable_id, filterIntegrableId)
      const matchIntegrableType = matchFilterKey(integrable_type, filterIntegrableType)
      const matchIntegrable = matchIntegrableId && matchIntegrableType

      return matchIntegrable && matchServiceProvider
    })

    return sortArrayBy(filtered, 'asc', 'service_provider')
  }, [integrationsUpdatedAt, JSON.stringify(filters)])

  const filteredIntegrationsCount = filteredIntegrations.length
  const hasFilteredIntegrations = !!filteredIntegrationsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredIntegrationsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingIntegrations } = useReduxAction(
    'integrations',
    'loadIntegrations',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredIntegrations,
    filteredIntegrationsCount,
    hasFilteredIntegrations,
    loading: loadingIntegrations,
  }
}

export default useIntegrations
